import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ShieldCheck, Upload, Download, FileText, AlertTriangle, CheckCircle2, Clock, Award, Sparkles, ExternalLink, CalendarClock, Database, Pencil, BellRing } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  valid: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: "VALID" },
  expiring_soon: { color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock, label: "EXPIRING <30D" },
  expired: { color: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle, label: "EXPIRED" },
  missing: { color: "bg-slate-100 text-slate-700 border-slate-200", icon: AlertTriangle, label: "MISSING" },
};

// Secondary row action — one consistent outlined style for Edit / PDF / Verify.
const ACTION_BTN = "h-7 px-2 text-xs bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300";

// Days-left colour: expired (negative) must be checked before "< 30", or it reads amber.
const daysTone = (d) => (d == null ? "text-gray-400" : d < 0 ? "text-red-700" : d < 30 ? "text-amber-700" : "text-emerald-700");

function Kpi({ label, value, icon: Icon, tone }) {
  const grad = { green: "from-emerald-400 to-emerald-600", amber: "from-amber-400 to-orange-500", red: "from-red-500 to-pink-600", blue: "from-sky-400 to-indigo-500" };
  return (
    <div className="glass px-3 py-3 rounded-lg flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${grad[tone]} flex items-center justify-center shrink-0 shadow-sm`}><Icon className="w-5 h-5 text-white" strokeWidth={2.25} /></div>
      <div className="min-w-0">
        <div className="text-2xl font-extrabold leading-none text-gray-900">{value}</div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1 truncate">{label}</div>
      </div>
    </div>
  );
}

export default function CertificateManagement() {
  const [data, setData] = useState({ items: [], kpis: {} });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [generated, setGenerated] = useState([]);
  const [schedule, setSchedule] = useState(null);
  // Issuing is platform-only (see certificate.py). Clients still see every certificate once
  // issued — they just cannot mint one for themselves.
  const { user } = useAuth();
  const canIssue = user?.role === "super_admin";
  const [busySite, setBusySite] = useState(null);
  const token = localStorage.getItem("f360_token");

  const load = () => Promise.all([
    api.get("/certificates").then((r) => setData(r.data)),
    api.get("/certificate/list").then((r) => setGenerated(r.data)).catch(() => {}),
    api.get("/certificate/schedule").then((r) => setSchedule(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const generateAcmsCert = async (siteId, formType = "B-1") => {
    setBusySite(siteId);
    try {
      const r = await api.post(`/certificate/${siteId}/generate`, null, { params: { form_type: formType } });
      toast.success(`Form ${formType} ${r.data.certificate_no} issued`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setBusySite(null); }
  };

  const downloadGenerated = (cert) => {
    fetch(`${API}/certificate/${cert.certificate_id}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `ACMS-${cert.certificate_no}.pdf`; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      acms_certificate_number: c.certificate_number || "",
      acms_certificate_expiry: (c.expiry || "").slice(0, 10),
      certifying_agency: c.certifying_agency || "",
      certifying_engineer: c.engineer_name || "",
      engineer_reg_number: c.engineer_reg || "",
      acms_certificate_scan: c.scan_url || "",
    });
  };

  const save = async () => {
    try {
      await api.put(`/certificates/${editing.site_id}`, form);
      toast.success("Certificate updated");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };

  const handleScanUpload = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("Max 6 MB"); return; }
    const reader = new FileReader();
    reader.onload = (e) => setForm((f) => ({ ...f, acms_certificate_scan: e.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSignatureUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Image only"); return; }
    if (file.size > 1024 * 1024) { toast.error("Max 1 MB"); return; }
    const reader = new FileReader();
    reader.onload = (e) => setForm((f) => ({ ...f, engineer_signature_url: e.target.result }));
    reader.readAsDataURL(file);
  };

  const downloadSelfCompliance = async (siteId) => {
    const res = await fetch(`${API}/certificates/${siteId}/self-compliance-pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `acms_certificate_${siteId}.pdf`;
    a.click();
    toast.success("Certificate PDF downloaded");
  };

  return (
    <div className="space-y-3" data-testid="cert-page">
      {/* Header — one compact row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-lg bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center shrink-0">
          <Award className="w-5 h-5" strokeWidth={2} />
        </div>
        <h1 className="text-xl md:text-2xl font-extrabold tracking-tight">ACMS Certificate Management</h1>
        <span className="text-[11px] text-gray-500">Maharashtra GR No. MFS-2023/C.R.370/UD-14 self-compliance · renewal reminders</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total Sites" value={data.kpis?.total || 0} icon={ShieldCheck} tone="blue" />
        <Kpi label="Valid" value={data.kpis?.valid || 0} icon={CheckCircle2} tone="green" />
        <Kpi label="Expiring < 30 days" value={data.kpis?.expiring_soon || 0} icon={Clock} tone="amber" />
        <Kpi label="Expired" value={data.kpis?.expired || 0} icon={AlertTriangle} tone="red" />
      </div>

      <div className="glass rounded-lg overflow-hidden">
        <div className="table-scroll">
          <Table>
            <TableHeader><TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead>Site</TableHead><TableHead>Cert No.</TableHead><TableHead>Expiry</TableHead><TableHead>Days Left</TableHead><TableHead>Agency / Engineer</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.items.map((c) => {
                const st = STATUS[c.status] || STATUS.missing;
                const StatusIcon = st.icon;
                return (
                  <TableRow key={c.site_id} className="border-gray-200" data-testid={`cert-row-${c.site_id}`}>
                    <TableCell className="font-semibold">{c.site_name}</TableCell>
                    <TableCell className="font-mono text-orange-700 text-xs">{c.certificate_number || "—"}</TableCell>
                    <TableCell className="text-xs">{c.expiry ? new Date(c.expiry).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className={`font-bold tabular-nums ${daysTone(c.days_left)}`}>
                      {c.days_left != null ? c.days_left + "d" : "—"}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>{c.certifying_agency || "—"}</div>
                      {c.engineer_name && <div className="text-gray-500">Eng. {c.engineer_name} {c.engineer_reg ? `(${c.engineer_reg})` : ""}</div>}
                    </TableCell>
                    <TableCell><Badge className={`${st.color} border text-[10px]`}><StatusIcon className="w-3 h-3 mr-1" />{st.label}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" onClick={() => openEdit(c)} data-testid={`edit-cert-${c.site_id}`} className={ACTION_BTN}><Pencil className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Edit</Button>
                        <Button size="sm" onClick={() => downloadSelfCompliance(c.site_id)} className={ACTION_BTN} data-testid={`pdf-cert-${c.site_id}`}><FileText className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> PDF</Button>
                        {canIssue && <Button size="sm" onClick={() => generateAcmsCert(c.site_id)} disabled={busySite === c.site_id} className="f360-gradient border-0 text-white h-7 px-2 text-xs" data-testid={`gen-cert-${c.site_id}`}><Sparkles className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> {busySite === c.site_id ? "Issuing…" : "Generate"}</Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!data.items.length && <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No sites.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="rounded-lg px-4 py-3 text-xs text-gray-600 bg-orange-50/60 border border-orange-100 flex items-start gap-2.5">
        <BellRing className="w-4 h-4 text-orange-500 shrink-0 mt-px" strokeWidth={2.25} />
        <div>
          <span className="font-semibold text-gray-800">Renewal reminders:</span> auto-emails are queued at 60, 30, 15 and 7 days before
          expiry to the building owner + certifying engineer. Configure recipients under <b>System Settings → Notifications</b>.
        </div>
      </div>

      {/* STATUTORY SCHEDULE — when each site's certificates are actually due */}
      {schedule && schedule.sites?.length > 0 && (
        <div className="glass rounded-lg overflow-hidden" data-testid="certificate-schedule">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="font-bold flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-orange-500" strokeWidth={2.5} /> Compliance Schedule
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Form A-1 is issued once, on installation. Form B-1 recurs every <b>January and July</b> and certifies
              the preceding six months — currently <b>{schedule.b1_period.start} → {schedule.b1_period.end}</b>,
              due <b>{schedule.b1_due_date}</b>.
            </p>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="border-gray-200 hover:bg-transparent">
                <TableHead>Site</TableHead>
                <TableHead>Form A-1</TableHead>
                <TableHead>Form B-1 — current period</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Evidence</TableHead>
                {canIssue && <TableHead className="text-right">Issue</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {schedule.sites.map((row) => {
                  const b1 = row.form_b1;
                  const tone = {
                    issued:   "bg-emerald-50 text-emerald-700 border-emerald-200",
                    overdue:  "bg-red-50 text-red-700 border-red-200",
                    due_now:  "bg-amber-50 text-amber-700 border-amber-200",
                    upcoming: "bg-slate-100 text-slate-700 border-slate-200",
                  }[b1.status];
                  // Coverage is measured, not promised: below 99% the period cannot support the
                  // uptime the Appendix asks for, so say so before anyone signs it.
                  const cov = row.evidence.coverage_pct;
                  const covTone = cov >= 99 ? "text-emerald-700" : cov >= 90 ? "text-amber-700" : "text-red-700";
                  return (
                    <TableRow key={row.site_id} className="border-gray-200">
                      <TableCell className="font-semibold">{row.site_name}</TableCell>
                      <TableCell>
                        {row.form_a1.status === "issued" ? (
                          <span className="text-xs text-emerald-700 font-semibold">{row.form_a1.certificate_no}</span>
                        ) : (
                          <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">NOT ISSUED</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${tone} text-[10px]`}>{b1.status.replace("_", " ").toUpperCase()}</Badge>
                        <div className="text-[11px] text-gray-500 mt-1">{b1.period_start} → {b1.period_end}</div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {b1.due_date}
                        <div className="text-[11px] text-gray-500">
                          {b1.days_remaining >= 0 ? `${b1.days_remaining} days left` : `${Math.abs(b1.days_remaining)} days overdue`}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={`text-xs font-bold ${covTone}`}>{cov}% coverage</div>
                        <div className="text-[11px] text-gray-500 flex items-center gap-1">
                          <Database className="w-3 h-3" />
                          {row.evidence.parameter_count} params · {row.evidence.days_with_data}/{row.evidence.period_days} days
                        </div>
                      </TableCell>
                      {canIssue && (
                        <TableCell className="text-right whitespace-nowrap">
                          <Button size="sm" className={`${ACTION_BTN} mr-1`}
                            disabled={busySite === row.site_id}
                            onClick={() => generateAcmsCert(row.site_id, "A-1")}>A-1</Button>
                          <Button size="sm" className="f360-gradient border-0 text-white h-7 px-2 text-xs"
                            disabled={busySite === row.site_id}
                            onClick={() => generateAcmsCert(row.site_id, "B-1")}>B-1</Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* GENERATED ACMS CERTIFICATES */}
      <div className="glass rounded-lg overflow-hidden" data-testid="generated-certificates">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-orange-500" strokeWidth={2.5} /> Issued ACMS Certificates</h2>
            <p className="text-xs text-gray-500 mt-1">Issued under the Maharashtra Fire Prevention and Life Safety Measures (Amendment) Rules, 2025 · publicly verifiable</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead>Certificate No.</TableHead><TableHead>Site</TableHead><TableHead>Issued</TableHead><TableHead>Valid Until</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {generated.map((c) => {
                const validDate = new Date(c.valid_until);
                const expired = validDate < new Date();
                const verifyUrl = `${API.replace(/\/api$/, "")}/api/public/cert/${c.certificate_no}`;
                return (
                  <TableRow key={c.certificate_id} className="border-gray-200" data-testid={`gen-cert-row-${c.certificate_no}`}>
                    <TableCell className="font-mono text-orange-700 text-xs">{c.certificate_no}</TableCell>
                    <TableCell className="font-semibold">{c.site_name}</TableCell>
                    <TableCell className="text-xs">{new Date(c.issued_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-xs">{validDate.toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Badge className={`border ${expired ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                        {expired ? "EXPIRED" : "VALID"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" onClick={() => downloadGenerated(c)} className={ACTION_BTN} data-testid={`dl-gen-${c.certificate_no}`}><Download className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> PDF</Button>
                        <a href={verifyUrl} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 rounded-md font-medium ${ACTION_BTN}`} data-testid={`verify-${c.certificate_no}`}><ExternalLink className="w-3 h-3" strokeWidth={2.5} /> Verify</a>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!generated.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">{canIssue ? <>No certificates issued yet. Click <b>Generate</b> on any site above to issue an ACMS Compliance Certificate.</> : <>No certificates issued for your sites yet. Certificates are issued by Fire360Pro and appear here automatically.</>}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Edit cert dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.site_name} — ACMS Certificate</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Certificate Number</Label><Input value={form.acms_certificate_number || ""} onChange={(e) => setForm({ ...form, acms_certificate_number: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" data-testid="cert-form-no" /></div>
            <div><Label>Expiry Date</Label><Input type="date" value={form.acms_certificate_expiry || ""} onChange={(e) => setForm({ ...form, acms_certificate_expiry: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" data-testid="cert-form-expiry" /></div>
            <div><Label>Certifying Agency</Label><Input value={form.certifying_agency || ""} onChange={(e) => setForm({ ...form, certifying_agency: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
            <div><Label>Engineer Name</Label><Input value={form.certifying_engineer || ""} onChange={(e) => setForm({ ...form, certifying_engineer: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
            <div><Label>Engineer Reg. Number</Label><Input value={form.engineer_reg_number || ""} onChange={(e) => setForm({ ...form, engineer_reg_number: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
            <div><Label>Engineer Signature (PNG, optional)</Label><Input type="file" accept="image/*" onChange={(e) => handleSignatureUpload(e.target.files?.[0])} className="bg-gray-50 border-gray-200 mt-1" /></div>
            <div className="col-span-2"><Label>Upload Certificate Scan (PDF/JPEG, max 6 MB)</Label><Input type="file" accept="image/*,application/pdf" onChange={(e) => handleScanUpload(e.target.files?.[0])} className="bg-gray-50 border-gray-200 mt-1" data-testid="cert-form-scan" /></div>
            {form.acms_certificate_scan && (
              <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded p-2 text-[11px] text-emerald-700">✓ Scan ready — {(form.acms_certificate_scan.length / 1024).toFixed(0)} kB</div>
            )}
            {form.engineer_signature_url && (
              <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded p-2 text-[11px] text-emerald-700">✓ Signature ready</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="f360-gradient border-0 text-white" data-testid="cert-form-save"><Upload className="w-4 h-4 mr-1" strokeWidth={2.5} /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
